// src/utils/db.js
import { openDB } from 'idb';

const DB_NAME = 'WappSyncDB';
const DB_VERSION = 2; // Increment version for new stores
const MSG_STORE = 'messages';
const META_STORE = 'metadata';
const MEDIA_STORE = 'media_cache'; // New store for media caching

export const initDB = async () => {
    return openDB(DB_NAME, DB_VERSION, {
        upgrade(db, oldVersion) {
            // Messages store
            if (!db.objectStoreNames.contains(MSG_STORE)) {
                db.createObjectStore(MSG_STORE, { keyPath: 'id' });
            }
            
            // Metadata store
            if (!db.objectStoreNames.contains(META_STORE)) {
                db.createObjectStore(META_STORE);
            }
            
            // Media cache store (new in version 2)
            if (!db.objectStoreNames.contains(MEDIA_STORE)) {
                const mediaStore = db.createObjectStore(MEDIA_STORE, { keyPath: 'url' });
                mediaStore.createIndex('timestamp', 'timestamp');
            }
        }
    });
};

// Save a single message immediately
export const saveMessage = async (message) => {
    try {
        const db = await initDB();
        const tx = db.transaction(MSG_STORE, 'readwrite');
        const store = tx.objectStore(MSG_STORE);
        await store.put(message);
        await tx.done;
        console.log('Message saved to IndexedDB:', message.id);
    } catch (error) {
        console.error('Error saving message to IndexedDB:', error);
    }
};

// Save multiple messages (batch operation)
export const saveMessages = async (messages) => {
    try {
        const db = await initDB();
        const tx = db.transaction(MSG_STORE, 'readwrite');
        const store = tx.objectStore(MSG_STORE);
        
        for (const msg of messages) {
            await store.put(msg);
        }
        
        await tx.done;
        console.log(`${messages.length} messages saved to IndexedDB`);
    } catch (error) {
        console.error('Error saving messages to IndexedDB:', error);
    }
};

export const getMessages = async () => {
    try {
        const db = await initDB();
        const messages = await db.getAll(MSG_STORE);
        // Sort messages by timestamp in descending order (newest first)
        return messages.sort((a, b) => b.timestamp - a.timestamp);
    } catch (error) {
        console.error('Error getting messages from IndexedDB:', error);
        return [];
    }
};

export const clearMessages = async () => {
    try {
        const db = await initDB();
        const tx = db.transaction(MSG_STORE, 'readwrite');
        await tx.objectStore(MSG_STORE).clear();
        await tx.done;
    } catch (error) {
        console.error('Error clearing messages from IndexedDB:', error);
    }
};

export const savePhone = async (phone) => {
    try {
        const db = await initDB();
        await db.put(META_STORE, phone, 'phone');
    } catch (error) {
        console.error('Error saving phone to IndexedDB:', error);
    }
};

export const getPhone = async () => {
    try {
        const db = await initDB();
        return await db.get(META_STORE, 'phone');
    } catch (error) {
        console.error('Error getting phone from IndexedDB:', error);
        return null;
    }
};

// Media caching functions
export const getCachedMedia = async (url) => {
    try {
        const db = await initDB();
        const cachedItem = await db.get(MEDIA_STORE, url);
        
        if (cachedItem) {
            // Check if media is older than 180 days
            const now = Date.now();
            const ageInDays = (now - cachedItem.timestamp) / (1000 * 60 * 60 * 24);
            
            if (ageInDays > 180) {
                // Remove expired media and re-download
                await db.delete(MEDIA_STORE, url);
                console.log('Removed expired cached media:', url);
            } else {
                // Return cached blob URL
                return URL.createObjectURL(cachedItem.blob);
            }
        }
        
        // Download and cache the media
        console.log('Downloading and caching media:', url);
        const response = await fetch(url);
        if (!response.ok) {
            throw new Error(`Failed to download media: ${url}`);
        }
        
        const blob = await response.blob();
        const mediaItem = {
            url: url,
            blob: blob,
            timestamp: Date.now()
        };
        
        // Save to cache
        await db.put(MEDIA_STORE, mediaItem);
        
        return URL.createObjectURL(blob);
    } catch (error) {
        console.error('Error with media caching:', error);
        // Fallback to original URL if caching fails
        return url;
    }
};

// Clean up expired media (older than 180 days)
export const cleanupExpiredMedia = async () => {
    try {
        const db = await initDB();
        const tx = db.transaction(MEDIA_STORE, 'readwrite');
        const store = tx.objectStore(MEDIA_STORE);
        const index = store.index('timestamp');
        
        const now = Date.now();
        const expiredThreshold = now - (180 * 24 * 60 * 60 * 1000); // 180 days ago
        
        const expiredItems = await index.getAll(IDBKeyRange.upperBound(expiredThreshold));
        
        for (const item of expiredItems) {
            await store.delete(item.url);
        }
        
        await tx.done;
        
        if (expiredItems.length > 0) {
            console.log(`Cleaned up ${expiredItems.length} expired media items`);
        }
    } catch (error) {
        console.error('Error cleaning up expired media:', error);
    }
};

// Get cache statistics
export const getCacheStats = async () => {
    try {
        const db = await initDB();
        const mediaItems = await db.getAll(MEDIA_STORE);
        const messages = await db.getAll(MSG_STORE);
        
        const totalMediaSize = mediaItems.reduce((total, item) => total + item.blob.size, 0);
        const oldestMedia = mediaItems.length > 0 ? 
            Math.min(...mediaItems.map(item => item.timestamp)) : null;
        const newestMedia = mediaItems.length > 0 ? 
            Math.max(...mediaItems.map(item => item.timestamp)) : null;
        
        return {
            messageCount: messages.length,
            mediaCount: mediaItems.length,
            totalMediaSize: totalMediaSize,
            totalMediaSizeMB: (totalMediaSize / (1024 * 1024)).toFixed(2),
            oldestMediaDate: oldestMedia ? new Date(oldestMedia) : null,
            newestMediaDate: newestMedia ? new Date(newestMedia) : null
        };
    } catch (error) {
        console.error('Error getting cache stats:', error);
        return null;
    }
};